import Pica from 'pica';
import { getFileExtension } from '../FileHelpers';
import { filenameWithNewExtension, createFile } from './file';

export const calculateDimensionsWithinConstraint = ({
  source,
  constraint,
}: {
  source: { width: number; height: number };
  constraint: { width: number; height: number };
}): { width: number; height: number } => {
  const sourceAspectRatio = source.width / source.height;
  const outputAspectRatio = constraint.width / constraint.height;

  if (sourceAspectRatio > outputAspectRatio) {
    return {
      width: constraint.width,
      height: constraint.width / sourceAspectRatio,
    };
  } else {
    return {
      height: constraint.height,
      width: constraint.height * sourceAspectRatio,
    };
  }
};

// work out the output file mime and file extension, can be either jpg or png
export const outputImageType = (
  filename: string
): { extension: string; mimeType: string } => {
  const fileExtension = getFileExtension(filename);

  switch (fileExtension) {
    case 'png':
      return {
        extension: 'png',
        mimeType: 'image/png',
      };

    case 'jpg':
    case 'jpeg':
    default:
      return {
        extension: 'jpg',
        mimeType: 'image/jpeg',
      };
  }
};

export const resizeImageFile = ({
  file,
  width,
  height,
  allowScaleUp = false,
}: {
  file: File;
  width: number;
  height?: number;
  allowScaleUp?: boolean;
}): Promise<File | Blob> =>
  new Promise((resolve, reject) => {
    const img = new Image();

    img.src = window.URL.createObjectURL(file);
    img.onload = () => {
      let constraintHeight = height;

      // if we want to resize the image based on its width only
      if (constraintHeight === undefined) {
        const sourceAspectRatio = img.width / img.height;
        constraintHeight = width / sourceAspectRatio;
      }

      // if the image dimensions is smaller than the target dimensions, don't resize
      if (img.width < width && img.height < constraintHeight && !allowScaleUp) {
        resolve(file);
      }

      const pica = new Pica();
      const canvas = document.createElement('canvas');
      const { extension, mimeType } = outputImageType(file.name);
      const outputFileName = filenameWithNewExtension(file.name, extension);

      // work out the dimensions for the output image
      const outputDimensions = calculateDimensionsWithinConstraint({
        source: { width: img.width, height: img.height },
        constraint: { width, height: constraintHeight },
      });
      canvas.height = outputDimensions.height;
      canvas.width = outputDimensions.width;

      // avoid "Index or size is negative or greater than the allowed amount" error
      img.height = img.height;
      img.width = img.width;

      // resize
      pica
        .resize(img, canvas, {
          unsharpAmount: 80,
          unsharpRadius: 0.6,
          unsharpThreshold: 2,
          alpha: true,
        })
        .then(
          (result) =>
            pica.toBlob(result, mimeType, 0.9).then((blob) => {
              resolve(createFile([blob], outputFileName, mimeType));
            }),
          (error) => reject(error)
        );
    };
  });
